import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { GlobalAlertBar } from './GlobalAlertBar';
import { StatusBar } from './StatusBar';
import { AlertBanner } from '@/shared/components/feedback/AlertBanner';
import { OfflineBanner } from '@/shared/components/feedback/OfflineBanner';
import { DemoBanner } from '@/shared/components/feedback/DemoBanner';
import { useDemoStore } from '@/shared/store/demo-store';
import { useKeyboardNav } from '@/shared/hooks/use-keyboard-nav';
import { useNotificationPush } from '@/shared/hooks/use-notification-push';
import { useSidebarCollapse } from '@/shared/hooks/use-sidebar-collapse';
import { OnboardingChecklistMount } from '@/features/onboarding/components/OnboardingChecklistMount';
import { RadixPointerEventsGuard } from '@/shared/components/feedback/RadixPointerEventsGuard';

export function AppLayout() {
  const { collapsed: sidebarCollapsed, toggle: toggleSidebar } = useSidebarCollapse();
  const isDemoMode = useDemoStore((s) => s.isDemoMode);
  useKeyboardNav();
  useNotificationPush();

  const sidebarWidth = sidebarCollapsed ? '56px' : '224px';

  return (
    <>
      <RadixPointerEventsGuard />
      <div
        className="grid h-screen w-screen overflow-hidden bg-background"
        style={{
          gridTemplateColumns: `${sidebarWidth} 1fr`,
          gridTemplateRows: 'auto 56px 1fr 28px',
          gridTemplateAreas: `
          "alert    alert"
          "sidebar  header"
          "sidebar  main"
          "status   status"
        `,
        }}
      >
        <div style={{ gridArea: 'alert' }} className="flex flex-col">
          {isDemoMode && <DemoBanner />}
          <OfflineBanner />
          <GlobalAlertBar />
        </div>

        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:shadow-lg focus:ring-2 focus:ring-primary"
        >
          Skip to main content
        </a>

        <div style={{ gridArea: 'sidebar' }} className="min-h-0 overflow-hidden">
          <Sidebar collapsed={sidebarCollapsed} />
        </div>

        <div style={{ gridArea: 'header' }} className="min-w-0">
          <Header onToggleSidebar={toggleSidebar} />
        </div>

        <div style={{ gridArea: 'main' }} className="flex min-h-0 min-w-0 overflow-hidden">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <AlertBanner />
            <main id="main-content" className="flex-1 overflow-y-auto pt-6 px-7 pb-10">
              <Outlet />
            </main>
          </div>
          <OnboardingChecklistMount />
        </div>

        <div style={{ gridArea: 'status' }} className="min-w-0">
          <StatusBar />
        </div>
      </div>
    </>
  );
}
